import React, {useCallback, useMemo, useState} from 'react';

import {SettingLabel} from '../components/SettingLabel';
import {BucketCustomization, SemanticLegendSettings} from '../types';
import {CategoryCustomizationInput} from './CategoryCustomizationInput';
import {SemanticLegendBucketsActions} from './SemanticLegendBucketsActions';
import {SemanticLegendsPalette} from './SemanticLegendsPalette';
import {SemanticLegendPalette} from './utils/colorPalettes';

// Static empty object to prevent re-renders
const EMPTY_CATEGORY_CUSTOMIZATION: BucketCustomization = {};

export type SemanticLegendCategoriesProps = {
  categories: string[];
  categoryCustomizations: Record<number, BucketCustomization>;
  configValuesCategories: string[];
  configValuesForRun: {runName: string; value: string}[];
  semanticLegendPalette: SemanticLegendPalette;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
};

export const SemanticLegendCategories = ({
  categories,
  categoryCustomizations,
  configValuesCategories,
  configValuesForRun,
  semanticLegendPalette,
  updateSetting,
}: SemanticLegendCategoriesProps) => {
  // Memoize categories list to prevent recreation
  const categoriesList = useMemo(() => {
    const configValues = configValuesForRun.map(
      configValue => configValue.value
    );
    const allValues = new Set([
      ...configValues,
      ...categories,
      ...configValuesCategories,
    ]);
    return Array.from(allValues);
  }, [configValuesForRun, configValuesCategories, categories]);

  const updateCategoryCustomizationSetting = useCallback(
    (settings: Partial<BucketCustomization>, index: number) => {
      const newCategoryCustomizations = {...categoryCustomizations};
      newCategoryCustomizations[index] = {
        ...newCategoryCustomizations[index],
        ...settings,
      };
      updateSetting({
        bucketCustomizations: newCategoryCustomizations,
        categories: categories,
      });
    },
    [updateSetting, categories, categoryCustomizations]
  );

  const updateCategoryValue = useCallback(
    (selected: {value: string; label: string} | null, index: number) => {
      if (selected) {
        const newCategories = [...categories];
        newCategories[index] = selected.value;
        updateSetting({categories: newCategories});
      }
    },
    [updateSetting, categories]
  );

  // Create stable callback handlers - this is the key performance fix
  const categoryUpdateHandlers = useMemo(() => {
    return categories.map((_, index) => ({
      updateCategorySetting: (settings: Partial<BucketCustomization>) =>
        updateCategoryCustomizationSetting(settings, index),
      updateCategoryValue: (selected: {value: string; label: string} | null) =>
        updateCategoryValue(selected, index),
    }));
  }, [categories, updateCategoryCustomizationSetting, updateCategoryValue]);

  // Memoize the category list to prevent re-creation on every render
  const categoryCustomizationList = useMemo(() => {
    return categories?.map((category, index) => {
      // Use static empty object instead of creating new one each time
      const categoryCustomization =
        categoryCustomizations[index] ?? EMPTY_CATEGORY_CUSTOMIZATION;

      return (
        <li key={`category-${index}-${category}`}>
          <CategoryCustomizationInput
            category={category}
            index={index}
            categoryCustomization={categoryCustomization}
            categoriesList={categoriesList}
            numberOfCurrentCategories={categories.length}
            semanticLegendPalette={semanticLegendPalette}
            updateCategorySetting={
              categoryUpdateHandlers[index].updateCategorySetting
            }
            updateCategoryValue={
              categoryUpdateHandlers[index].updateCategoryValue
            }
          />
        </li>
      );
    });
  }, [
    categories,
    categoriesList,
    categoryCustomizations,
    semanticLegendPalette,
    categoryUpdateHandlers,
  ]);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex w-full gap-8">
        <SettingLabel text="Categories" className="flex w-full" />
        <SemanticLegendsPalette />
        <SemanticLegendBucketsActions setIsOpen={setIsOpen} isOpen={isOpen} />
      </div>
      <ul className="flex flex-col gap-8">{categoryCustomizationList}</ul>
    </div>
  );
};
